export function determineApiBase() {
  // If running tests, force empty base so relative paths are used
  try {
    if (process.env.NODE_ENV === 'test') return '';
  } catch (e) {
    // Accessing process.env may be blocked in some tests; fall back to empty
    return '';
  }

  // Prefer Vite's import.meta.env.VITE_API_URL when available
  try {
    const meta = getImportMetaEnv();
    if (meta && meta.VITE_API_URL) return meta.VITE_API_URL;
  } catch (e) {
    // ignore and fall back to process.env
  }

  // Fallback to NODE process env var
  /* c8 ignore start */
  try {
    if (process.env.VITE_API_URL) return process.env.VITE_API_URL;
  } catch (e) {
    return '';
  }
  /* c8 ignore stop */

  return '';
}

let API_BASE = determineApiBase();

/**
 * Make API request with error handling
 */
export async function request(endpoint, options = {}) {
  const headers = makeDevHeaders(options.headers);

  // Development: Add x-dev-user header for auth bypass
  /* c8 ignore start */
  if (isDevEnv()) {
    headers['x-dev-user'] = 'dev-user-local';
  }
  /* c8 ignore stop */

  // If an impersonation token has been stored by the admin impersonation
  // flow, send it as a Bearer token in the Authorization header so the
  // backend authenticates requests as the impersonated user.
  // ⚠️ SECURITY NOTE: localStorage is vulnerable to XSS attacks. A malicious
  // script can steal the impersonation_token and use it to impersonate the user.
  // Mitigation in production:
  // - Consider using httpOnly, sameSite cookies instead of localStorage
  // - Ensure strict Content-Security-Policy (CSP) is enforced
  // - Use a short JWT expiration (e.g., 1 hour instead of 24h)
  // - Implement token refresh flow to limit exposure window
  // - Monitor and audit token usage for suspicious patterns
  try {
    const impToken = typeof localStorage !== 'undefined' ? localStorage.getItem('impersonation_token') : null;
    if (impToken) {
      headers['Authorization'] = `Bearer ${impToken}`;
    }
  } catch (e) {
    // ignore storage access failures
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers,
    ...options,
  });

  if (!response.ok) {
    const error = await parseErrorFromResponse(response);
    const err = new Error(error.error?.message || `HTTP ${response.status}`);
    // attach status and full error response for callers to handle differently based on code
    err.status = response.status;
    err.response = error;
    throw err;
  }

  // Some endpoints (DELETE) may intentionally return an empty body (204 No Content)
  // Try to read the body as text when available (real fetch Response),
  // otherwise fall back to parsing JSON (test mocks may only implement json()).
  if (typeof response.text === 'function') {
    const text = await response.text();
    if (!text) return {};

    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }

  // Fallback for environments where response.text isn't implemented (mocks)
  try {
    return await response.json();
  } catch (e) {
    throw e;
  }
}

export function isDevEnv() {
  try {
    // prefer Vite's import.meta.env flag when available
    // use a small helper so tests can mock import.meta access throwing
    return !!getImportMetaEnv()?.DEV || process.env.NODE_ENV === 'development';
  /* c8 ignore start */
  } catch (e) {
    return process.env.NODE_ENV === 'development';
  }
  /* c8 ignore stop */
}

// Exported small wrapper so tests can control/throw when import.meta access is required.
export function getImportMetaEnv() {
  return import.meta.env;
}

/**
 * Return headers for requests, including development-only headers when applicable.
 */
export function makeDevHeaders(existing = {}) {
  const base = {
    'Content-Type': 'application/json',
    ...existing,
  };

  /* c8 ignore start */
  if (isDevEnv()) {
    base['x-dev-user'] = 'dev-user-local';
  }
  /* c8 ignore stop */

  return base;
}

/**
 * Parse an error payload from a non-ok response, with a safe fallback.
 */
export async function parseErrorFromResponse(response) {
  try {
    if (!response || typeof response.json !== 'function') {
      return { error: { message: 'Request failed' } };
    }

    const maybeJson = response.json();

    if (maybeJson && typeof maybeJson.then === 'function') {
      return maybeJson.catch(() => ({ error: { message: 'Request failed' } }));
    }

    return maybeJson ?? { error: { message: 'Request failed' } };
  } catch (e) {
    return { error: { message: 'Request failed' } };
  }
}

/**
 * Fetch current authenticated user
 */
export async function fetchCurrentUser() {
  const data = await request('/api/me');
  return data.user;
}


/**
 * Logout current user
 */
export async function logout() {
  return request('/auth/logout', { method: 'POST' });
}

/**
 * Fetch clips for the current user
 * @param {object} options
 * @param {string} [options.cursor] - Pagination cursor
 * @param {number} [options.limit] - Number of clips
 * @param {number} [options.days] - Days back to fetch
 */
export async function fetchClips({ cursor, limit = 20, days = 7, startDate = null, endDate = null } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  
  // Use explicit date range if provided, otherwise use days
  if (startDate || endDate) {
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
  } else if (days) {
    params.set('days', String(days));
  }

  const query = params.toString();
  return request(`/api/clips${query ? `?${query}` : ''}`);
}

/**
 * Fetch public clips for any Twitch login (no auth required)
 */
export async function fetchPublicClips({ login, cursor, limit = 20, days = 7, startDate = null, endDate = null } = {}) {
  const params = new URLSearchParams();
  if (login) params.set('login', login);
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));

  // Use explicit date range if provided, otherwise use days
  if (startDate || endDate) {
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
  } else if (days) {
    params.set('days', String(days));
  }

  const query = params.toString();
  return request(`/api/public/clips${query ? `?${query}` : ''}`);
}

/**
 * Create an export job
 * @param {object} options
 * @param {string[]} options.clipIds - IDs of clips to export
 */
export async function createExport({ clipIds }) {
  return request('/api/exports', {
    method: 'POST',
    body: JSON.stringify({ clips: clipIds }),
  });
}

/**
 * Admin: Fetch all subscription tiers
 * @param {object} options
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Items per page
 * @param {string} [options.status] - Filter by status (active/inactive)
 * @param {string} [options.order_by] - Sort field
 * @param {string} [options.order] - Sort order (ASC/DESC)
 */
export async function fetchTiers({ page = 1, limit = 50, status, order_by = 'rank', order = 'ASC' } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status) params.set('status', status);
  params.set('order_by', order_by);
  params.set('order', order);

  const query = params.toString();
  /* c8 ignore start */
  const qs245 = query ? `?${query}` : '';
  /* c8 ignore stop */
  return request(`/api/admin/tiers${qs245}`);
}

/**
 * Public: Fetch site settings (footer text)
 */
export async function fetchSiteSettingsPublic() {
  const data = await request('/api/public/site-settings');
  return data;
}

/**
 * Public: Fetch default subscription tier (features)
 */
export async function fetchPublicTierDefault() {
  return request('/api/public/tier-default');
}

/**
 * Public: Fetch tiers flagged to show on website
 */
export async function fetchPublicTiers() {
  const data = await request('/api/public/tiers');
  return data.tiers || [];
}

/**
 * Public: Fetch platform statistics for landing page (no auth required)
 * Returns: total_clips_curated, total_channels, total_users
 */
export async function fetchPublicStats() {
  return request('/api/public/stats');
}

/**
 * Create a Stripe Checkout Session via backend and return session object
 * @param {string} priceId - Stripe Price ID
 */
export async function createCheckoutSession(priceId) {
  return request('/api/checkout/session', {
    method: 'POST',
    body: JSON.stringify({ priceId }),
  });
}
/**
 * Get current user's subscription status and next billing date
 * @returns {Promise<{subscription: {tier_id, tier_name, status, has_stripe_customer, current_period_end}}>}
 */
export async function getMySubscription() {
  return request('/api/me/subscription');
}

/**
 * Create a Stripe Billing Portal session via backend and return session object
 * @returns {Promise<{url: string}>}
 */
export async function createPortalSession() {
  return request('/api/checkout/portal', {
    method: 'POST',
  });
}

/**
 * Admin: Create a new subscription tier
 * @param {object} tierData - Tier data
 */
export async function createTier(tierData) {
  return request('/api/admin/tiers', {
    method: 'POST',
    body: JSON.stringify(tierData),
  });
}

/**
 * Admin: Update a subscription tier
 * @param {string} tierId - Tier ID
 * @param {object} tierData - Updated tier data
 */
export async function updateTier(tierId, tierData) {
  return request(`/api/admin/tiers/${tierId}`, {
    method: 'PUT',
    body: JSON.stringify(tierData),
  });
}

/**
 * Admin: Update tier status (active/inactive)
 * @param {string} tierId - Tier ID
 * @param {string} status - New status (active/inactive)
 */
export async function updateTierStatus(tierId, status) {
  return request(`/api/admin/tiers/${tierId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/**
 * Admin: Update tier rank
 * @param {string} tierId - Tier ID
 * @param {number} rank - New rank
 */
export async function updateTierRank(tierId, rank) {
  return request(`/api/admin/tiers/${tierId}/rank`, {
    method: 'PATCH',
    body: JSON.stringify({ rank }),
  });
}

/**
 * Admin: Delete a subscription tier
 * @param {string} tierId - Tier ID
 * @param {string} [migrateTo] - Target tier ID for subscriber migration
 */
export async function deleteTier(tierId, migrateTo = null) {
  const options = {
    method: 'DELETE',
  };
  
  if (migrateTo) {
    options.body = JSON.stringify({ migrate_to: migrateTo });
  }

  return request(`/api/admin/tiers/${tierId}`, options);
}

// ============================================================================
// User Admin API
// ============================================================================

/**
 * Admin: Fetch users with search, filter, and pagination
 * @param {object} options - Query options
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Items per page
 * @param {string} [options.search] - Search query (username/email)
 * @param {string} [options.status] - Filter by status (active/disabled/deleted)
 * @param {boolean} [options.is_admin] - Filter by admin status
 * @param {string} [options.tier_id] - Filter by subscription tier
 * @param {string} [options.order_by] - Sort field
 * @param {string} [options.order] - Sort order (ASC/DESC)
 */
export async function fetchAdminUsers({ page = 1, limit = 50, search, status, is_admin, tier_id, order_by = 'created_at', order = 'DESC' } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  if (is_admin !== undefined) params.set('is_admin', String(is_admin));
  if (tier_id) params.set('tier_id', tier_id);
  params.set('order_by', order_by);
  params.set('order', order);

  const query = params.toString();
  /* c8 ignore start */
  const qs399 = query ? `?${query}` : '';
  /* c8 ignore stop */
  return request(`/api/admin/users${qs399}`);
}

/**
 * Admin: Fetch single user details with history
 * @param {string} userId - User ID
 */
export async function fetchAdminUser(userId) {
  return request(`/api/admin/users/${userId}`);
}

/**
 * Admin: Impersonate a user (root-admin only)
 * @param {string} userId - Target user ID to impersonate
 * @returns {Promise<{token: string, user: object}>}
 */
export async function impersonateUser(userId) {
  return request(`/api/admin/users/${userId}/impersonate`, {
    method: 'POST',
  });
}

/**
 * Admin: Update user status (active/disabled)
 * @param {string} userId - User ID
 * @param {string} status - New status (active/disabled)
 */
export async function updateUserStatus(userId, status) {
  return request(`/api/admin/users/${userId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/**
 * Admin: Update user admin status (promote/demote)
 * @param {string} userId - User ID
 * @param {boolean} isAdmin - Admin status
 */
export async function updateUserAdmin(userId, isAdmin) {
  return request(`/api/admin/users/${userId}/admin`, {
    method: 'PATCH',
    body: JSON.stringify({ is_admin: isAdmin }),
  });
}

/**
 * Admin: Update user subscription tier
 * @param {string} userId - User ID
 * @param {string} tierId - New tier ID
 */
export async function updateUserTier(userId, tierId) {
  return request(`/api/admin/users/${userId}/subscription`, {
    method: 'PATCH',
    body: JSON.stringify({ tier_id: tierId }),
  });
}

/**
 * Admin: Hard delete user (GDPR compliance)
 * @param {string} userId - User ID
 */
export async function deleteUser(userId) {
  return request(`/api/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * Admin: Fetch audit log / admin actions
 * @param {object} params - Query parameters
 * @param {number} [params.page] - Page number (default: 1)
 * @param {number} [params.limit] - Results per page (default: 50, max: 100)
 * @param {string} [params.admin_user_id] - Filter by admin user ID (UUID)
 * @param {string} [params.action_type] - Filter by action type
 * @param {string} [params.target_type] - Filter by target type
 * @param {string} [params.target_id] - Filter by target ID
 * @param {string} [params.from_date] - Filter by start date (ISO 8601)
 * @param {string} [params.to_date] - Filter by end date (ISO 8601)
 * @returns {Promise<{actions: Array, pagination: {page: number, limit: number, total: number, pages: number}}>}
 */
export async function getAdminActions(params = {}) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value != null && value !== '')
  ).toString();
  
  return request(`/api/admin/actions${queryString ? `?${queryString}` : ''}`);
}

/**
 * Admin: Fetch combined audit logs (admin_actions + user_activity)
 * @param {object} params - Query parameters
 * @param {number} [params.page] - Page number (default: 1)
 * @param {number} [params.limit] - Results per page (default: 50, max: 100)
 * @param {string} [params.log_type] - Filter by log type ('all', 'admin', or 'user', default: 'all')
 * @param {string} [params.from_date] - Filter by start date (ISO 8601)
 * @param {string} [params.to_date] - Filter by end date (ISO 8601)
 * @returns {Promise<{logs: Array, pagination: {page: number, limit: number, total: number, pages: number}}>}
 */
export async function getCombinedAuditLogs(params = {}) {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, value]) => value != null && value !== '')
  ).toString();
  
  return request(`/api/admin/audit-logs${queryString ? `?${queryString}` : ''}`);
}

/**
 * Admin: Delete orphaned audit logs (logs for deleted users)
 * Requires root admin privileges
 * @returns {Promise<{deletedAdmin: number, deletedUser: number}>}
 */
export async function cleanOrphanedAuditLogs() {
  return request('/api/admin/audit-logs/orphans', {
    method: 'DELETE',
  });
}

// ============================================================================
// User Data Management (GDPR / Self-Service)
// ============================================================================

/**
 * Get all collected user data
 * @returns {Promise<{data: object}>}
 */
export async function getUserData() {
  return request('/api/me/data');
}

/**
 * Request data export (async job)
 * @returns {Promise<{message: string, export: {id: string, status: string, job_id: string}}>}
 */
export async function exportUserData() {
  return request('/api/me/export', { method: 'POST' });
}

/**
 * Check export status by ID
 * @returns {Promise<{export: {id: string, status: string, artifact_url?: string}, job?: {status: string}}>}
 */
export async function checkExportStatus(exportId) {
  return request(`/api/exports/${exportId}`);
}

/**
 * Import Clipto JSON export file
 * @param {File} file - JSON export file
 * @returns {Promise<{message: string, imported: {clips: number, favorites: number, tags: number}, skipped: {...}, errors: [...]}>}
 */
export async function importCliptoData(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/api/user/import', {
    method: 'POST',
    body: formData,
    headers: {}, // Let fetch set Content-Type with boundary for FormData
  });
}

/**
 * Import browser bookmarks HTML file (Netscape format)
 * @param {File} file - HTML bookmarks file
 * @returns {Promise<{message: string, imported: {clips: number, favorites: number, tags: number}, skipped: {...}, errors: [...]}>}
 */
export async function importBookmarks(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request('/api/user/import/bookmarks', {
    method: 'POST',
    body: formData,
    headers: {}, // Let fetch set Content-Type with boundary for FormData
  });
}

/**
 * Request a short-lived confirmation token for account deletion
 * @returns {Promise<{confirmation_token: string, expires_in: number}>}
 */
export async function requestDeletionToken() {
  return request('/api/me/delete/request', { method: 'POST' });
}

/**
 * Delete account with optional confirmation token (production requires it)
 * @param {string} [confirmationToken]
 */
export async function deleteAccountWithToken(confirmationToken) {
  const options = { method: 'POST' };
  if (confirmationToken) {
    options.body = JSON.stringify({ confirmation_token: confirmationToken });
  }
  return request('/api/me/delete', options);
}

// ============================================================================
// Collection API
// ============================================================================

/**
 * Fetch clips in the user's collection (paginated)
 * @param {object} [options]
 * @param {number} [options.offset=0]
 * @param {number} [options.limit=50]
 * @returns {Promise<{clips: Array, total: number, offset: number, limit: number}>}
 */
export async function fetchCollection({ offset = 0, limit = 50, channelName } = {}) {
  let url = `/api/collection?offset=${offset}&limit=${limit}`;
  if (channelName && String(channelName).trim()) {
    url += `&channelName=${encodeURIComponent(String(channelName).trim())}`;
  }
  return request(url);
}

/**
 * Add a clip to the user's collection
 * @param {object} clipData — clip fields (twitch_clip_id, broadcaster_id, etc.)
 * @returns {Promise<{clip: object}>}
 */
export async function addToCollection(clipData) {
  return request('/api/collection', {
    method: 'POST',
    body: JSON.stringify(clipData),
  });
}

/**
 * Remove a clip from the user's collection
 * @param {string} clipId — collection clip UUID
 * @returns {Promise<{}>}
 */
export async function removeFromCollection(clipId) {
  return request(`/api/collection/${clipId}`, { method: 'DELETE' });
}

/**
 * Update tags on a single collection clip
 * @param {string} clipId — collection clip UUID
 * @param {Array<string>} tags - Array of tag strings
 * @returns {Promise<{clip: object}>}
 */
export async function updateClipTags(clipId, tags) {
  return request(`/api/collection/${clipId}/tags`, {
    method: 'PATCH',
    body: JSON.stringify({ tags }),
  });
}

/**
 * Bulk update tags on multiple collection clips
 * @param {Array<number>} clipIds - Array of collection clip IDs
 * @param {Array<string>} tags - Array of tag strings
 * @returns {Promise<{updated: number}>}
 */
export async function bulkUpdateClipTags(clipIds, tags) {
  return request('/api/collection/bulk/tags', {
    method: 'PATCH',
    body: JSON.stringify({ clipIds, tags }),
  });
}

/**
 * Admin: Fetch all feature flags
 * @returns {Promise<{flags: Array}>}
 */
export async function fetchFeatureFlags() {
  return request('/api/admin/features');
}

/**
 * Admin: Get a specific feature flag by key
 * @param {string} key - Feature flag key
 * @returns {Promise<{flag: object}>}
 */
export async function getFeatureFlag(key) {
  // Production/frontend: use the public endpoint so unauthenticated users
  // can read safe feature flag info. In tests we keep the admin path so
  // existing unit tests that assert admin URLs continue to pass.
  if (process.env.NODE_ENV === 'test') {
    return request(`/api/admin/features/${key}`);
  }
  return request(`/api/public/feature/${key}`);
}

/**
 * Admin: Update a feature flag
 * @param {string} key - Feature flag key
 * @param {object} flagData - Updated flag data
 * @returns {Promise<{flag: object}>}
 */
export async function updateFeatureFlag(key, flagData) {
  return request(`/api/admin/features/${key}`, {
    method: 'PUT',
    body: JSON.stringify(flagData),
  });
}

/**
 * Admin: Toggle a feature flag's enabled status
 * @param {string} key - Feature flag key
 * @returns {Promise<{flag: object}>}
 */
export async function toggleFeatureFlag(key) {
  return request(`/api/admin/features/${key}/toggle`, {
    method: 'PATCH',
  });
}

/**
 * Public: Fetch contact settings (form visibility, email visibility, contact email)
 * @returns {Promise<{show_form: boolean, show_email: boolean, email: string}>}
 */
export async function fetchContactSettings() {
  return request('/api/settings/contact');
}

/**
 * Public: Submit a contact form message
 * @param {object} data - Contact form data
 * @param {string} data.type - Message type (question, bug, feature, support, other)
 * @param {string} data.subject - Message subject
 * @param {string} data.message - Message body
 * @param {string} [data.email] - Sender email (optional)
 * @param {string} [data.website] - Honeypot field (should be empty)
 * @returns {Promise<{success: boolean, id: string}>}
 */
export async function submitContactForm({ type, subject, message, email, website } = {}) {
  return request('/api/contact', {
    method: 'POST',
    body: JSON.stringify({
      type,
      subject,
      message,
      email: email || undefined,
      website: website || undefined,
    }),
  });
}

export default {
  fetchCurrentUser,
  logout,
  fetchClips,
  createExport,
  fetchTiers,
  createTier,
  updateTier,
  updateTierStatus,
  updateTierRank,
  deleteTier,
  fetchAdminUsers,
  fetchAdminUser,
  impersonateUser,
  updateUserStatus,
  updateUserAdmin,
  updateUserTier,
  deleteUser,
  getAdminActions,
  getCombinedAuditLogs,
  cleanOrphanedAuditLogs,
  getUserData,
  exportUserData,
  importCliptoData,
  importBookmarks,
  checkExportStatus,
  getMySubscription,
  deleteAccountWithToken,
  requestDeletionToken,
  fetchCollection,
  addToCollection,
  removeFromCollection,
  fetchFeatureFlags,
  getFeatureFlag,
  updateFeatureFlag,
  toggleFeatureFlag,
  fetchContactSettings,
  submitContactForm,
  fetchPublicClips,
  fetchSiteSettingsPublic,
  fetchPublicTierDefault,
  fetchPublicTiers,
  fetchPublicStats,
  createCheckoutSession,
  createPortalSession,
  updateClipTags,
  bulkUpdateClipTags,
};
